import { type ApolloClient, gql } from '@apollo/client/core';
import type { Query } from '../../../generated/api.graphql';
import type { VPError } from '../../../VPError';
import type { VPResult } from '../../VPResult';
import * as Category from './Category';
import { CATEGORY_FIELDS, SUBCATEGORY_FIELDS, TYPE_FIELDS } from './fragments';
import * as Type from './Type';

/**
 * The genre catalog on the tenant (AppSchema) connection — the read behind the announce modal's
 * Event Type / Category / Sub-category pickers (`admin/src/components/announce-modal/GenreSection.vue`).
 *
 * Read-only by design. ADR-009 moved genre *administration* to InternalSchema (`api/Internal/Genre/`),
 * and this read stays on `gqlApiClient`: the internal connection gates on staff, so following the
 * catalog there would answer every non-superadmin operator with a 401.
 */

export type Genres = {
  types: Type.Type[];
  categories: Category.Category[];
};

export const GENRES_QUERY = gql`
  query genres {
    genreTypes {
      ...TypeFields
    }
    genreCategories {
      ...CategoryFields
      subcategories {
        ...SubcategoryFields
      }
    }
  }
  ${TYPE_FIELDS}
  ${CATEGORY_FIELDS}
  ${SUBCATEGORY_FIELDS}
`;

export const fetchGenres = async (
  client: ApolloClient<Query>,
): Promise<VPResult<Genres, VPError[]>> => {
  try {
    const { data } = await client.query<Query>({
      query: GENRES_QUERY,
      fetchPolicy: 'network-only',
    });

    return {
      success: true,
      data: {
        // Both are `[T!]!`, so the backend cannot answer with one list missing — an error there
        // nulls the whole payload, which `client.query` raises into the catch below. The fallbacks
        // are belt-and-braces on the wire shape, not a failure path.
        types: (data?.genreTypes ?? []).map(Type.fromGql),
        categories: (data?.genreCategories ?? []).map(Category.fromGql),
      },
    };
  } catch (error) {
    return {
      success: false,
      errors: [{ path: '', message: error instanceof Error ? error.message : 'Request failed' }],
    };
  }
};
